// Copyright (c) Docugami, Inc. All rights reserved.

import { TextEntryDialog } from "components/shell/utils/TextEntryDialog";
import { useEffect, useState } from "react";
import { useAppDispatch } from 'hooks/redux';
import { reportErrorEncountered } from "store/authentication/actions";
import { RenameWorkPathDialogVisibilitySchema, RenamedWorkPathSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { WorkPath } from "networking/WorkPathApi";
import { useGetWorkPathsQuery, useRenameWorkPathMutation } from "store/work_path/workPathSlice";

interface RenameWorkPathDialogProps {
    workPath: WorkPath;
    onComplete: (renamed: boolean) => void;
}

/**
 * Presents a text entry dialog box to allow a user to rename a work path.  A work path name must be unique
 * across all existing work paths.
 * @param props
 */
export const RenameWorkPathDialog = (props: RenameWorkPathDialogProps): JSX.Element => {
    const [isRenaming, setIsRenaming] = useState(false);

    const dispatch = useAppDispatch();

    const [trigger] = useRenameWorkPathMutation();

    const { isUninitialized, currentData: workPaths } = useGetWorkPathsQuery();

    useEffect(() => {
        Telemetry.trackEvent<RenameWorkPathDialogVisibilitySchema>({
            isVisible: true,
            eventName: TelemetryEvent.RenameWorkPathDialogVisibility
        });
        return () => Telemetry.trackEvent<RenameWorkPathDialogVisibilitySchema>({
            isVisible: false,
            eventName: TelemetryEvent.RenameWorkPathDialogVisibility
        });
    }, []);

    async function onConfirm(name: string) {
        if (isRenaming) {
            return;
        }

        setIsRenaming(true);

        let renamed = false;
        if (name !== props.workPath.name) {
            const previousName = props.workPath.name;
            try {
                Telemetry.trackEvent<RenamedWorkPathSchema>({
                    workPathId: props.workPath.id,
                    previousName: previousName,
                    name: name,
                    eventName: TelemetryEvent.RenamedWorkPath
                });

                await trigger({
                    workPathId: props.workPath.id,
                    name: name
                });
                renamed = true;
            }
            catch (error) {
                dispatch(reportErrorEncountered(`Failed to rename WorkPath. ID: ${props.workPath.id}`, error as Error));
            }
        }

        setIsRenaming(false);
        props.onComplete(renamed);
    }

    function validateWorkPathName(originalWorkPath: WorkPath, name: string) {
        const trimmedName = name.trim()

        if (!Boolean(trimmedName)) {
            return false
        }

        if (isUninitialized || !workPaths) {
            return false;
        }
        else {
            for (const workPathId of workPaths.ids) {
                const workPath = workPaths.entities[workPathId];
                if (trimmedName === workPath?.name.trim()) {
                    return false;
                }
            }
        }

        return true;
    }

    return <TextEntryDialog
        hidden={false}
        originalText={props.workPath.name}
        placeholderText="Enter a name"
        title="Rename your project"
        confirmButtonText="Rename"
        dismissButtonText="Cancel"
        confirmedButtonDisabled={(name) => isRenaming || !validateWorkPathName(props.workPath, name)}
        confirmedCallback={onConfirm}
        dismissedCallback={() => props.onComplete(false)} />
}
